import { createContext, useCallback, useContext, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { authApi, UNAUTHORIZED_EVENT, type Me } from '../api/client'
import { setDisplayZone } from '../lib/format'

const MeContext = createContext<Me | null>(null)

export function useMe() {
  return useContext(MeContext)
}

type State = 'loading' | 'anonymous' | 'authenticated'

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>('loading')
  const [me, setMe] = useState<Me | null>(null)

  const refresh = useCallback(async () => {
    try {
      const who = await authApi.me()
      setDisplayZone(who.time_zone) // before anything shows a time
      setMe(who)
      setState('authenticated')
    } catch {
      setMe(null)
      setState('anonymous')
    }
  }, [])

  useEffect(() => {
    refresh()
    const onUnauthorized = () => {
      setMe(null)
      setState('anonymous')
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [refresh])

  if (state === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>
  }
  if (state === 'anonymous') {
    return <LoginPage onLoggedIn={refresh} />
  }
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>
}

function LoginPage({ onLoggedIn }: { onLoggedIn: () => void }) {
  const authError = new URLSearchParams(window.location.search).get('auth_error')
  const [showLocal, setShowLocal] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submitLocal = async (e: FormEvent) => {
    e.preventDefault()
    if (!username || !password) {
      setError('Enter username and password')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await authApi.localLogin(username, password)
      setPassword('')
      onLoggedIn()
    } catch {
      setError('Login failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm border rounded-lg bg-card p-6 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">xControl</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to continue</p>
        </div>

        {authError && <p className="text-sm text-[#A3271D]">{authError}</p>}

        <a
          href="/auth/login"
          className="block w-full text-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Sign in with login.bosner.de
        </a>

        <button
          type="button"
          onClick={() => setShowLocal(!showLocal)}
          className="w-full text-sm text-muted-foreground hover:text-foreground"
        >
          {showLocal ? 'Hide local login' : 'Local / emergency login'}
        </button>

        {showLocal && (
          <form onSubmit={submitLocal} className="space-y-3">
            <input
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="Username (not the email)"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value)
                setError(null)
              }}
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError(null)
              }}
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
            {error && <p className="text-sm text-[#A3271D]">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 disabled:opacity-50"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
